import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';

import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableConfig, TableColumn, TableColumnTypes, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { ObservationsRegistration } from 'src/app/performance/models/performance.model';

import { getObservations } from 'src/app/performance/store/selectors/performance.selector';
import Utils from 'src/app/utils';

import CycleUtils from '../../cycle-utils';
import { BREEDS } from '../../models/breeds';
import {
  Breed,
  Cycle,
  Departure,
  DepartureAllocation,
  DepartureAllocationTableRow,
  Gender,
  Order,
  PropagationCompany,
  Section,
} from '../../models/cycle.model';

import { CycleService } from '../../services/cycle.service';
import { setSelectedDepartureId } from '../../store/actions/cycle.actions';
import {
  getPropagationCompanies,
  getSections,
  getSelectedCycle,
  getSelectedDeparture,
} from '../../store/selectors/cycle.selectors';

export interface DepartureItemDialogInputData extends AbstractFormDialogData {
  departureId?: number;
  observations: ObservationsRegistration[];
}
export interface DepartureItemDialogResultData {
  departure: Departure;
}

@Component({
  selector: 'app-departure-item-dialog',
  templateUrl: './departure-item-dialog.component.html',
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    TableComponent,
    AgrNumberDirective,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DepartureItemDialogComponent
  extends AbstractDialog<DepartureItemDialogResultData>
  implements OnInit
{
  id: number;

  readonly formGroup = new FormGroup({
    departureDate: new FormControl<string>('', [Validators.required]),
    propagationCompany: new FormControl<PropagationCompany>(undefined, [Validators.required]),
    breed: new FormControl<Breed>(undefined, [Validators.required]),
    gender: new FormControl<Gender>(undefined, [Validators.required]),
    dateOfMeasurement: new FormControl<string>('', [Validators.required]),
    weight: new FormControl<number>({ value: undefined, disabled: true }),
    uniformity: new FormControl<number>({ value: undefined, disabled: true }),
    cvPercentage: new FormControl<number>({ value: undefined, disabled: true }),
  });
  readonly numberOfAnimalsArray = new FormArray([], [Validators.required]);

  readonly breeds: Breed[] = BREEDS;
  readonly genders: Gender[] = [Gender.Female, Gender.Male];
  readonly orders$ = this.store.select(getSelectedCycle).pipe(map((c) => c.orders));
  readonly sections$ = this.store.select(getSections);
  readonly propagationCompanies$ = this.store.select(getPropagationCompanies);
  readonly filteredPropagationCompanies$ = new BehaviorSubject<PropagationCompany[]>([]);
  readonly filteredBreeds$ = new BehaviorSubject<Breed[]>([]);
  readonly filteredGenders$ = new BehaviorSubject<Gender[]>([]);

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: false,
    wrapperWidth: '100%',
  };
  allocationsTableConfig: TableConfig;
  allocationsTableColumns: TableColumn[] = [];
  allocations: DepartureAllocationTableRow[] = [];
  isValidSumOfAllocationsAmounts = true;

  readonly cycleStartDate$ = this.store.select(getSelectedCycle).pipe(
    take(1),
    map((selectedCycle) => this.cycleService.getCycleStartDate(selectedCycle)),
  );

  private readonly currentCycleState$: Observable<Record<string, number>> = this.store
    .select(getSelectedCycle)
    .pipe(
      withLatestFrom(
        this.store.select(getObservations),
        this.store.select(getPropagationCompanies),
      ),
      take(1),
      map(([selectedCycle, observations, propagationCompanies]) => {
        const currentCycle: Cycle = {
          ...selectedCycle,
          departures: selectedCycle.departures.filter((m) => m.id !== this.id),
        };

        const transitions = CycleUtils.getAnimalTransitions(currentCycle, observations);
        // This state-check, checks whether all numbers add up, including observations.
        let state = CycleUtils.getState(transitions);
        if (state instanceof Error) {
          return undefined;
        }
        // This state-check, checks whether no animals with different genders and breeds are mixed up.
        state = this.cycleService.getAndValidateCycleState(
          currentCycle,
          observations,
          propagationCompanies,
        );
        return state instanceof Error ? undefined : state;
      }),
    );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DepartureItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      DepartureItemDialogComponent,
      AbstractDialogResultData<DepartureItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly cycleService: CycleService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
    this.id = this.data.departureId;
    this.orders$
      .pipe(withLatestFrom(this.propagationCompanies$), take(1))
      .subscribe(([orders, propagationCompanies]) => {
        this.filteredPropagationCompanies$.next(
          propagationCompanies.filter((p) => orders.some((o) => o.propagationCompany.id === p.id)),
        );
      });
  }

  ngOnInit(): void {
    if (this.id > 0) {
      this.data.title = this.localeService.translate('departure.edit-departure');

      this.store.dispatch(setSelectedDepartureId({ id: this.id }));
      this.store
        .select(getSelectedDeparture)
        .pipe(
          withLatestFrom(this.sections$, this.propagationCompanies$, this.currentCycleState$),
          skipWhile(
            ([departure, sections, propagationCompanies, _]) =>
              departure === null ||
              departure === undefined ||
              sections?.length === 0 ||
              propagationCompanies?.length === 0,
          ),
          take(1),
        )
        .subscribe(([departure, _sections, propagationCompanies, _currentCycleState]) => {
          this.formGroup.controls.departureDate.setValue(departure.departureDate);
          this.filteredGenders$.next([departure.gender]);
          this.filteredBreeds$.next([departure.breed]);
          this.formGroup.controls.propagationCompany.setValue(
            propagationCompanies.find((pc) => pc.id === departure.propagationCompany.id),
          );

          this.changeDetectorRef.detectChanges();
          this.formGroup.controls.breed.setValue(departure.breed);
          this.formGroup.controls.gender.setValue(departure.gender);
          this.changeDetectorRef.detectChanges();
          this.propagationCompanySelectionChanged();

          this.formGroup.controls.dateOfMeasurement.setValue(departure.dateOfMeasurement);
          this.initAllocationsTable();
          this.setAllocationsTableDatasource(
            this.formGroup.controls.breed.value,
            this.formGroup.controls.gender.value,
            departure.propagationCompany,
            departure.allocations,
          );
          // this.onDateOfMeasurementChanged(new Date(departure.dateOfMeasurement));
        });
    } else {
      this.data.title = this.localeService.translate('departure.add-departure');
      this.initAllocationsTable();
    }
  }

  private initAllocationsTable() {
    this.allocationsTableColumns = [
      {
        id: 'sectionName',
        type: TableColumnTypes.STRING,
        label: this.localeService.translate('arrival.allocations-table.section'),
        disableSort: true,
      },
      {
        id: 'numberOfAnimals',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.localeService.translate('arrival.allocations-table.amount-of-chicks'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.numberOfAnimalsArray,
          step: 1,
        },
      },
    ];
    this.allocationsTableConfig = {
      ...this.tableConfigBase,
      columns: this.allocationsTableColumns,
      dataSource: new MatTableDataSource(this.allocations),
    };
  }

  private setAllocationsTableDatasource(
    breed: Breed,
    gender: Gender,
    propagationCompany: PropagationCompany,
    allocations?: DepartureAllocation[],
  ) {
    this.allocations = [];
    const validators: ValidatorFn[] = [];

    if (!!breed && !!gender) {
      this.store
        .select(getSections)
        .pipe(withLatestFrom(this.orders$, this.currentCycleState$), take(1))
        .subscribe(
          ([sections, orders, currentCycleState]: [
            Section[],
            Order[],
            { [key: string]: number },
          ]) => {
            sections
              .filter(
                (s) =>
                  s.gender === gender &&
                  s.breed.id === breed.id &&
                  orders.some(
                    (o) =>
                      o.propagationCompany.id === propagationCompany.id &&
                      o.breed.id === breed.id &&
                      ((o.numberOfFemales > 0 && gender === Gender.Female) ||
                        (o.numberOfMales > 0 && gender === Gender.Male)),
                  ),
              )
              .forEach((section, index) => {
                let maxNumberOfAnimals = 0;
                const key = `${section.id}_${propagationCompany.id}`;
                if (key) {
                  maxNumberOfAnimals = currentCycleState[key];
                }

                if (maxNumberOfAnimals > 0) {
                  this.allocations.push({
                    id: index,
                    sectionId: section.id,
                    sectionName: `${section.name} (${section.barn.name})`,
                    numberOfAnimals: allocations
                      ? (allocations.find((a) => a.id === index)?.numberOfAnimals ??
                        maxNumberOfAnimals)
                      : maxNumberOfAnimals,
                  });

                  validators.push(Validators.max(maxNumberOfAnimals));
                }
              });
            this.changeDetectorRef.detectChanges();
            this.allocationsTableConfig.dataSource.data = this.allocations;
            this.changeDetectorRef.detectChanges();
            this.allocations.forEach((_, i) => {
              this.numberOfAnimalsArray.controls[i].setValidators(validators[i]);
              this.numberOfAnimalsArray.controls[i].valueChanges.subscribe((_) => {
                if (this.formGroup.controls.dateOfMeasurement.value) {
                  this.onDateOfMeasurementChanged(
                    new Date(this.formGroup.controls.dateOfMeasurement.value),
                  );
                }
              });
              this.numberOfAnimalsArray.controls[i].updateValueAndValidity();
            });
          },
        );
    }
  }

  onDateOfMeasurementChanged(date: Date) {
    this.cycleStartDate$.pipe(take(1)).subscribe((cycleStartDate) => {
      let animalWeightInGrams = 0;
      let uniformity = 0;
      let coefficientOfVariation = 0;
      let totalAnimals = 0;
      let failed = false;

      if (cycleStartDate && date) {
        const dayToTakeMeasurementFrom = CycleUtils.getDaysDifferenceBetweenTwoDates(
          cycleStartDate,
          date,
        );

        let rows = <DepartureAllocationTableRow[]>(
          this.allocationsTableConfig.dataSource.data.map((row: DepartureAllocationTableRow) =>
            Object.assign({}, row),
          )
        );
        rows = rows.map((row) => ({
          ...row,
          numberOfAnimals: this.numberOfAnimalsArray.controls[row.id]?.value,
        }));

        if (dayToTakeMeasurementFrom !== undefined && rows.length > 0) {
          for (const row of rows) {
            const observation = this.data.observations.find(
              (o) => o.sectionId === row.sectionId && o.day === dayToTakeMeasurementFrom,
            );
            if (observation) {
              animalWeightInGrams += observation.animalWeightInGrams * row.numberOfAnimals;
              uniformity += observation.uniformity * row.numberOfAnimals;
              coefficientOfVariation += observation.coefficientOfVariation * row.numberOfAnimals;
              totalAnimals += row.numberOfAnimals;
            } else {
              failed = true;
              break;
            }
          }
        } else {
          failed = true;
        }
      } else {
        failed = true;
      }

      if (!failed) {
        this.formGroup.controls.weight.setValue(animalWeightInGrams / totalAnimals);
        this.formGroup.controls.uniformity.setValue(uniformity / totalAnimals);
        this.formGroup.controls.cvPercentage.setValue(coefficientOfVariation / totalAnimals);
      } else {
        this.formGroup.controls.weight.setValue(null);
        this.formGroup.controls.uniformity.setValue(null);
        this.formGroup.controls.cvPercentage.setValue(null);
      }
    });
  }

  propagationCompanySelectionChanged() {
    this.orders$.pipe(take(1)).subscribe((orders) => {
      const filteredOrders = orders.filter(
        (o) => o.propagationCompany.id === this.formGroup.controls.propagationCompany.value.id,
      );

      const currentBreedId = this.formGroup.controls.breed.value?.id ?? 0;
      const currentGender = this.formGroup.controls.gender.value;

      const filteredBreeds = this.breeds.filter((b) =>
        filteredOrders.some((o) => b.id === o.breed.id),
      );
      this.filteredBreeds$.next(filteredBreeds);

      const genders: Gender[] = [];
      if (filteredOrders.some((o) => o.numberOfMales ?? 0 > 0)) {
        genders.push(Gender.Male);
      }
      if (filteredOrders.some((o) => o.numberOfFemales ?? 0 > 0)) {
        genders.push(Gender.Female);
      }
      this.filteredGenders$.next(genders);

      const existingCurrentBreed = filteredBreeds.find((b) => b.id === currentBreedId);
      const existingCurrentGender = genders.find((g) => g === currentGender);
      this.formGroup.controls.breed.setValue(existingCurrentBreed);
      this.formGroup.controls.gender.setValue(existingCurrentGender);
    });
  }

  selectionChanged() {
    if (
      !!this.formGroup.controls.breed.value &&
      !!this.formGroup.controls.gender.value &&
      !!this.formGroup.controls.propagationCompany.value
    ) {
      this.setAllocationsTableDatasource(
        this.formGroup.controls.breed.value,
        this.formGroup.controls.gender.value,
        this.formGroup.controls.propagationCompany.value,
        [],
      );
    }
    this.changeDetectorRef.detectChanges();
    this.onDateOfMeasurementChanged(new Date(this.formGroup.controls.dateOfMeasurement.value));
  }

  save() {
    if (this.formGroup.valid) {
      this.sections$.pipe(take(1)).subscribe((sections) => {
        let rows = <DepartureAllocationTableRow[]>(
          this.allocationsTableConfig.dataSource.data.map((row: DepartureAllocationTableRow) =>
            Object.assign({}, row),
          )
        );
        rows = rows.map((row, index) => ({
          ...row,
          numberOfAnimals: this.numberOfAnimalsArray.controls[index].value,
        }));

        const totalDeparture = rows.reduce(
          (acc: number, section: DepartureAllocationTableRow) => acc + section.numberOfAnimals,
          0,
        );
        if (totalDeparture > 0) {
          const departure: Departure = {
            id: this.id,
            departureDate:
              typeof (<unknown>this.formGroup.controls.departureDate.value) !== 'string'
                ? Utils.toUtcDate(
                    new Date(this.formGroup.controls.departureDate.value),
                  ).toISOString()
                : <string>this.formGroup.controls.departureDate.value,
            propagationCompany: this.formGroup.controls.propagationCompany.value,
            breed: this.formGroup.controls.breed.value,
            gender: this.formGroup.controls.gender.value,
            dateOfMeasurement:
              typeof (<unknown>this.formGroup.controls.dateOfMeasurement.value) !== 'string'
                ? Utils.toUtcDate(
                    new Date(this.formGroup.controls.dateOfMeasurement.value),
                  ).toISOString()
                : <string>this.formGroup.controls.dateOfMeasurement.value,
            allocations: rows.map((row: DepartureAllocationTableRow) => ({
              id: row.id,
              section: sections.find((s) => s.id === row.sectionId),
              numberOfAnimals: row.numberOfAnimals,
            })),
          };
          this.confirm({ departure });
        }
      });
    }
  }
}
